import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { Printer, ArrowLeft, CheckCircle2, Clock, XCircle } from "lucide-react";
import { toast } from "sonner";

const METHOD_LABEL = { cash: "Contanti in agenzia", bank: "Bonifico bancario", stripe: "Carta di credito (Stripe)", paypal: "PayPal" };

const STATUS_META = {
  paid: { label: "Pagato", color: "emerald", Icon: CheckCircle2 },
  pending: { label: "In attesa di conferma", color: "amber", Icon: Clock },
  expired: { label: "Sessione scaduta", color: "slate", Icon: XCircle },
  cancelled: { label: "Annullato", color: "red", Icon: XCircle },
};

export default function PaymentReceipt() {
  const { txId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/payments/${txId}/receipt`)
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(formatApiError(e)); });
  }, [txId]);

  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400">Caricamento…</div>;

  const tx = data.transaction;
  const policy = data.policy || {};
  const client = data.client || {};
  const agency = data.agency || {};
  const meta = STATUS_META[tx.payment_status] || STATUS_META.pending;
  const Icon = meta.Icon;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="receipt-page">
      <header className="bg-[#0A192F] text-white print:hidden">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <button onClick={() => navigate("/area-riservata")} className="text-xs text-slate-300 inline-flex items-center gap-1 hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> Torna all&apos;area riservata
          </button>
          <button
            data-testid="print-receipt"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Printer className="h-3.5 w-3.5" /> Stampa ricevuta
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 print:px-0 print:py-0">
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden print:border-0 print:shadow-none" id="receipt-content">
          {/* Receipt header (agency branding) */}
          <div className="bg-[#0A192F] text-white px-8 py-6 print:bg-white print:text-[#0A192F] print:border-b-2 print:border-[#0A192F]">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                {agency.logo_url && <img src={agency.logo_url} alt="logo" className="h-12 w-12 rounded object-cover" />}
                <div>
                  <h1 className="font-display text-2xl font-semibold tracking-tight">{agency.name || "Assicura"}</h1>
                  {agency.legal_name && <p className="text-xs opacity-70">{agency.legal_name}</p>}
                </div>
              </div>
              <div className="text-right text-xs">
                {agency.address && <div>{agency.address}{agency.house_number ? ` ${agency.house_number}` : ""}</div>}
                {agency.city && <div>{agency.cap} {agency.city} {agency.province ? `(${agency.province})` : ""}</div>}
                {agency.vat_number && <div>P.IVA {agency.vat_number}</div>}
                {agency.rui_number && <div>RUI {agency.rui_number}{agency.rui_date ? ` · ${formatDate(agency.rui_date)}` : ""}</div>}
              </div>
            </div>
          </div>

          <div className="px-8 py-6">
            {/* Status badge */}
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-1">Ricevuta di pagamento</p>
                <h2 className="font-display text-3xl font-semibold">N° {tx.id.slice(0, 8).toUpperCase()}</h2>
              </div>
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-${meta.color}-100 text-${meta.color}-700 text-sm font-semibold`} data-testid="receipt-status">
                <Icon className="h-4 w-4" />
                {meta.label}
              </div>
            </div>

            {/* Date & method */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 pb-6 border-b border-slate-200 text-sm">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Data emissione</p>
                <p className="font-medium mt-0.5">{formatDate(tx.created_at)} · {new Date(tx.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</p>
              </div>
              {tx.paid_at && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Data pagamento</p>
                  <p className="font-medium mt-0.5">{formatDate(tx.paid_at)} · {new Date(tx.paid_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</p>
                </div>
              )}
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Metodo</p>
                <p className="font-medium mt-0.5">{METHOD_LABEL[tx.method] || tx.method}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Causale</p>
                <p className="font-mono text-xs mt-1">{tx.reference}</p>
              </div>
            </div>

            {/* Client + Policy */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6 pb-6 border-b border-slate-200 text-sm">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Pagatore</p>
                <p className="font-semibold">{client.surname} {client.name}</p>
                {client.fiscal_code && <p className="text-xs text-slate-500 font-mono">{client.fiscal_code}</p>}
                {client.email && <p className="text-xs text-slate-500 mt-1">{client.email}</p>}
                {client.address && (
                  <p className="text-xs text-slate-500 mt-1">
                    {client.address} {client.house_number || ""}<br />
                    {client.cap} {client.city} {client.province ? `(${client.province})` : ""}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Polizza</p>
                <p className="font-semibold">n° {policy.policy_number}</p>
                <p className="text-xs text-slate-500 capitalize">{policy.type}</p>
                {policy.company && <p className="text-xs text-slate-500">Compagnia: {policy.company}</p>}
                {policy.end_date && <p className="text-xs text-slate-500 mt-1">Scadenza {formatDate(policy.end_date)}</p>}
                {policy.vehicle_plate && <p className="text-xs text-slate-500 mt-1">Veicolo: <span className="font-mono">{policy.vehicle_plate}</span></p>}
              </div>
            </div>

            {/* Amount */}
            <div className="bg-slate-50 border border-slate-200 rounded-md p-6 flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Totale pagato</span>
              <span className="font-display text-4xl font-semibold tabular-nums" data-testid="receipt-amount">{formatCurrency(tx.amount)}</span>
            </div>

            {/* Footer */}
            <div className="mt-6 text-xs text-slate-500 space-y-1">
              <p>Documento generato automaticamente. {tx.payment_status === "paid" ? "Il pagamento è stato confermato." : "La transazione è in attesa di conferma dall'agenzia."}</p>
              {tx.payment_status === "paid" && tx.paid_at && (
                <p>Conferma agenzia ricevuta in data {formatDate(tx.paid_at)}.</p>
              )}
              <p className="pt-2 border-t border-slate-100 mt-3">{agency.name} · Ricevuta non fiscale ai sensi della normativa vigente · Validità contabile soggetta a fatturazione separata se richiesta.</p>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        @media print {
          @page { margin: 1.5cm; }
          body { background: white !important; }
          [data-testid="receipt-page"] { background: white !important; }
        }
      `}</style>
    </div>
  );
}
